import React, { type FC, useEffect, useState } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { useStores } from '../hooks/useStores';
import { createDriverInDb } from '../services/driverService';

interface CreateDriverModalProps {
    isOpen: boolean;
    onClose: () => void;
    onRefresh: () => void;
    defaultStoreId?: string;
}

const FIELD = 'w-full border border-stone-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-offset-0 focus:ring-[#ee2b74] transition bg-white';
const LABEL = 'block text-sm font-semibold text-stone-700 mb-2';

const EMPTY_FORM = {
    email: '', password: '', first_name: '', last_name: '',
    cellphone: '', car_model: '', number_plate: '', store_id: '',
};

const CreateDriverModal: FC<CreateDriverModalProps> = ({ isOpen, onClose, onRefresh, defaultStoreId }) => {
    const { stores, fetchStores } = useStores();

    const [saving,    setSaving]   = useState(false);
    const [error,     setError]    = useState<string | null>(null);
    const [formData,  setFormData] = useState(EMPTY_FORM);

    useEffect(() => {
        if (isOpen) {
            if (stores.length === 0) void fetchStores();
            if (defaultStoreId && defaultStoreId !== 'all') {
                setFormData(prev => ({ ...prev, store_id: defaultStoreId }));
            }
        }
    }, [isOpen, defaultStoreId, fetchStores, stores.length]);

    if (!isOpen) return null;

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setSaving(true);
        setError(null);
        try {
            await createDriverInDb({
                email:        formData.email.trim(),
                password:     formData.password,
                first_name:   formData.first_name.trim(),
                last_name:    formData.last_name.trim(),
                cellphone:    formData.cellphone.trim(),
                car_model:    formData.car_model.trim(),
                number_plate: formData.number_plate.trim().toUpperCase(),
                store_id:     formData.store_id || null,
            });
            onRefresh();
            onClose();
            setFormData(EMPTY_FORM);
        } catch (err) {
            const msg = err instanceof Error ? err.message : 'Failed to create driver';
            if (msg.includes('already registered') || msg.includes('23505')) {
                setError(`A driver with the email "${formData.email}" already exists.`);
            } else {
                setError(msg);
            }
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-stone-900/40 backdrop-blur-sm flex items-center justify-center z-[9999] p-4">
            <div className="bg-white w-full max-w-2xl rounded-2xl shadow-sm max-h-[90vh] overflow-y-auto relative">
                <div className="h-1 bg-[#ee2b74] w-full" />

                <div className="sticky top-0 bg-white border-b border-stone-200 px-6 py-4 flex items-center justify-between z-10">
                    <h2 className="text-lg font-semibold text-stone-900">Add New Driver</h2>
                    <button onClick={onClose} className="p-2 rounded-lg text-stone-400 hover:bg-stone-100 hover:text-stone-600 transition-colors">
                        <XMarkIcon className="h-5 w-5" />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="p-6">
                    {error && (
                        <div className="mb-6 p-4 bg-amber-50 border-l-4 border-amber-500 rounded-r-lg flex items-start gap-3">
                            <svg className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                            </svg>
                            <div className="flex-1">
                                <h3 className="text-sm font-bold text-amber-800">Error</h3>
                                <p className="text-xs text-amber-700 mt-1 leading-relaxed">{error}</p>
                            </div>
                            <button type="button" onClick={() => setError(null)} className="text-amber-400 hover:text-amber-600">
                                <XMarkIcon className="h-4 w-4" />
                            </button>
                        </div>
                    )}

                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className={LABEL}>First Name</label>
                            <input name="first_name" required className={FIELD} value={formData.first_name} onChange={handleChange} placeholder="e.g. John" />
                        </div>
                        <div>
                            <label className={LABEL}>Last Name</label>
                            <input name="last_name" required className={FIELD} value={formData.last_name} onChange={handleChange} placeholder="e.g. Doe" />
                        </div>
                        <div>
                            <label className={LABEL}>Email (Login)</label>
                            <input type="email" name="email" required className={FIELD} value={formData.email} onChange={handleChange} placeholder="driver@email.com" />
                        </div>
                        <div>
                            <label className={LABEL}>Password</label>
                            <input type="password" name="password" required className={FIELD} value={formData.password} onChange={handleChange} placeholder="••••••••" />
                        </div>
                        <div className="col-span-2">
                            <label className={LABEL}>Cellphone Number</label>
                            <input type="tel" name="cellphone" required className={FIELD} value={formData.cellphone} onChange={handleChange} placeholder="e.g. 081 234 5678" />
                        </div>
                        <div className="col-span-2">
                            <label className={LABEL}>Store Assignment</label>
                            <select name="store_id" required className={FIELD} value={formData.store_id} onChange={handleChange}>
                                <option value="">— Select store —</option>
                                {stores.map(s => (
                                    <option key={s.id} value={s.id}>{s.name}</option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label className={LABEL}>Car Model</label>
                            <input name="car_model" required className={FIELD} value={formData.car_model} onChange={handleChange} placeholder="e.g. Toyota Corolla" />
                        </div>
                        <div>
                            <label className={LABEL}>Number Plate</label>
                            <input name="number_plate" required className={FIELD} value={formData.number_plate} onChange={handleChange} placeholder="e.g. ABC 123 GP" />
                        </div>
                    </div>

                    <div className="flex justify-end gap-3 pt-6 mt-6 border-t border-stone-200">
                        <button type="button" onClick={onClose}
                                className="px-4 py-2.5 rounded-lg border border-stone-300 text-stone-700 text-sm font-medium hover:bg-stone-50 transition-colors">
                            Cancel
                        </button>
                        <button type="submit" disabled={saving}
                                className="px-4 py-2.5 rounded-lg bg-[#ee2b74] text-white text-sm font-medium hover:bg-[#d4245f] transition-colors disabled:opacity-60">
                            {saving ? 'Creating…' : 'Create Driver'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default CreateDriverModal;